import React, { useState, useRef, useCallback, useMemo, useEffect } from 'react';
import './TaskTable.css';
import '../TaskRow/TaskRow.css';
import TaskRow from '../TaskRow/TaskRow';
import TaskFilters from '../../../../features/filters/components/TaskFilters/TaskFilters';
import { useTasksContext } from '../../../../shared/contexts/TasksContext';
import { useAuth } from '../../../../features/auth/contexts/AuthContext';
import { useFilterContext } from '../../../../features/filters/contexts/FilterContext';
import { useTaskEditing } from '../../hooks/useTaskEditing';
import { useTaskHandlers } from '../../hooks/useTaskHandlers';
import { useTaskDeletion } from '../../hooks/useTaskDeletion';
import { useTaskExpansion } from '../../hooks/useTaskExpansion';
import { useTaskStatus } from '../../hooks/useTaskStatus';
import { useFileNameEditing } from '../../../../features/files/hooks/useFileNameEditing';
import { useTaskAutoFocus } from '../../hooks/useTaskAutoFocus';
import { useBlockingTasksHighlight } from '../../hooks/useBlockingTasksHighlight';
import { usePrioritizeSections } from '../../hooks/usePrioritizeSections';
import { useTaskDragDropWithSequential } from '../../hooks/useTaskDragDropWithSequential';
import { usePrioritizeDragDrop } from '../../hooks/usePrioritizeDragDrop';
import TaskTablePrioritizeView from './TaskTablePrioritizeView';
import TaskTableStandardView from './TaskTableStandardView';
import FeatureShowcase from '../../../../ui/components/FeatureShowcase/FeatureShowcase';
import { getTopToolbarFlag } from '../../../../shared/constants/featureFlags';
import {
  buildTaskTree,
  filterTaskTree,
  groupTasksByPrioritize,
  calculateOrderForParent,
  findNextSibling,
  findPreviousSibling,
  getEffectivePrioritizeSection
} from '../../utils/taskUtils';
import { isSequentialTask, getSequenceNumber } from '../../utils/sequentialTasks';

/** Keep completed tasks visible in Prioritize view for this long (completion animation 1600ms). */
const PRIORITIZE_COMPLETION_VISIBLE_MS = 850;

function TaskTable() {
  const { isLoggedIn } = useAuth();
  const { filter, setFilter, timeFilter, setTimeFilter } = useFilterContext();
  const {
    tasks,
    loading,
    currentFileId,
    files,
    hasPendingChangesForCurrentFile,
    hasRestoredDraft,
    getLoggedOutWorkspaceSnapshot,
    updateFileName,
    fetchTasks,
    updateTaskStatus,
    updateTaskText,
    updateTaskTextLocal,
    updateTaskDueDate,
    updateTaskOrder,
    updateTaskOrders,
    updateTaskParentWithTextAndOrder,
    createTask,
    deleteTask: deleteTaskAPI
  } = useTasksContext();

  // Feature showcase (logged-out): minimized state for layout padding when position:fixed
  const [isShowcaseMinimized, setIsShowcaseMinimized] = useState(true);

  // Keep responsive behaviour in sync across the whole table (not only per-row).
  const [isMobileViewport, setIsMobileViewport] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia('(max-width: 768px)').matches : false
  );
  useEffect(() => {
    const mql = window.matchMedia('(max-width: 768px)');
    const handler = () => setIsMobileViewport(mql.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);

  const topToolbarFlag = getTopToolbarFlag({ isMobileViewport });

  // Placeholder row (when no tasks): checkbox state so new task keeps it when created
  const [placeholderStatus, setPlaceholderStatus] = useState('incomplete');
  const placeholderStatusRef = useRef(placeholderStatus);
  placeholderStatusRef.current = placeholderStatus;

  // Placeholder due date (when no tasks): set via toolbar, shown in button and passed to new task on create
  const [placeholderDueDate, setPlaceholderDueDate] = useState(null);
  const [placeholderIsLaterDate, setPlaceholderIsLaterDate] = useState(false);
  const placeholderDueDateRef = useRef(placeholderDueDate);
  placeholderDueDateRef.current = placeholderDueDate;
  const placeholderIsLaterDateRef = useRef(placeholderIsLaterDate);
  placeholderIsLaterDateRef.current = placeholderIsLaterDate;

  // When true, placeholder focus handler will not move caret to start (used when restoring focus after due date set)
  const skipPlaceholderCaretToStartRef = useRef(false);

  // Track if placeholder has at least one character (for mobile "Add task" row visibility)
  const [placeholderHasContent, setPlaceholderHasContent] = useState(false);

  // In Prioritize view, keep completed tasks visible until animation + delay
  const [recentlyCompletedPrioritizeIds, setRecentlyCompletedPrioritizeIds] = useState(() => new Set());
  const prioritizeCompletionTimeoutsRef = useRef(new Map());

  // In Prioritize view, highlight tasks that just moved to a different section (due date change, status change, etc.)
  const [recentlyMovedPrioritizeIds, setRecentlyMovedPrioritizeIds] = useState(() => new Set());
  const prioritizeMovedTimeoutsRef = useRef(new Map());
  const previousPrioritizeSectionByTaskIdRef = useRef(new Map());
  const visiblePrioritizeSectionsRef = useRef(new Set());
  const tasksRef = useRef([]);
  const currentFileIdRef = useRef(currentFileId);
  tasksRef.current = tasks;
  currentFileIdRef.current = currentFileId;

  // Pending delete: highlight task with pink background, then delete after 200ms
  const [pendingDeleteTaskId, setPendingDeleteTaskId] = useState(null);
  const [pendingDeleteOrigin, setPendingDeleteOrigin] = useState(null);

  useEffect(() => {
    return () => {
      prioritizeCompletionTimeoutsRef.current.forEach(id => clearTimeout(id));
      prioritizeCompletionTimeoutsRef.current.clear();
      prioritizeMovedTimeoutsRef.current.forEach(id => clearTimeout(id));
      prioritizeMovedTimeoutsRef.current.clear();
    };
  }, []);

  // Task editing hook
  const {
    editingTaskId,
    setEditingTaskId,
    editingDueDateTaskId,
    setEditingDueDateTaskId,
    editingElementRef,
    dateInputCallbackRef,
    caretAtZeroTaskIdsRef
  } = useTaskEditing(tasks);

  // Task expansion hook
  const {
    expandedTasks,
    setExpandedTasks,
    toggleExpand
  } = useTaskExpansion({
    tasks,
    currentFileId,
    updateTaskStatus
  });

  const pendingFocusRef = useRef(null);
  const editHighlightAnchorRef = useRef(null);
  const [editHighlightAnchorKey, setEditHighlightAnchorKey] = useState(0);

  const onEditHighlightAnchorReady = useCallback(() => {
    setEditHighlightAnchorKey((k) => k + 1);
  }, []);

  // Task deletion hook
  const { handleDeleteTask } = useTaskDeletion({
    tasks,
    expandedTasks,
    editingTaskId,
    setEditingTaskId,
    editingElementRef,
    updateTaskOrder,
    updateTaskOrders,
    updateTaskParentWithTextAndOrder,
    deleteTaskAPI,
    isLoggedIn,
    pendingFocusRef
  });

  // File name editing hook
  const {
    editingFileName,
    currentFileName,
    handleFileNameClick,
    handleFileNameBlur,
    handleFileNameKeyDown,
    focusFileName
  } = useFileNameEditing({
    files,
    currentFileId,
    updateFileName,
    tasks,
    expandedTasks,
    setEditingTaskId,
    editingElementRef,
    pendingFocusRef
  });

  useEffect(() => {
    if (!editingTaskId && !editingFileName) {
      editHighlightAnchorRef.current = null;
    }
  }, [editingTaskId, editingFileName]);

  // Clear placeholder-has-content when focus leaves the placeholder
  useEffect(() => {
    if (editingTaskId !== 'placeholder_client_key') setPlaceholderHasContent(false);
  }, [editingTaskId]);

  // Prioritize sections hook
  const {
    visiblePrioritizeSections,
    togglePrioritizeSectionVisibility
  } = usePrioritizeSections();

  useEffect(() => {
    visiblePrioritizeSectionsRef.current = visiblePrioritizeSections;
  }, [visiblePrioritizeSections]);

  // Task handlers hook (text editing, etc.)
  const {
    handleTaskTextClick,
    handleTaskTextFocus,
    handleTaskTextInput,
    handleTaskContentClick,
    handleTaskTextPaste,
    handleTaskTextBlur,
    handleTaskTextKeyDown,
    tryFocusPending,
    focusRequestId
  } = useTaskHandlers({
    tasks,
    expandedTasks,
    setExpandedTasks,
    editingTaskId,
    setEditingTaskId,
    editingElementRef,
    updateTaskText,
    updateTaskTextLocal,
    updateTaskStatus,
    updateTaskDueDate,
    updateTaskOrder,
    updateTaskOrders,
    updateTaskParentWithTextAndOrder,
    createTask,
    deleteTask: handleDeleteTask,
    caretAtZeroTaskIdsRef,
    focusFileName,
    pendingFocusRef,
    placeholderStatusRef,
    placeholderDueDateRef,
    placeholderIsLaterDateRef,
    skipPlaceholderCaretToStartRef,
    filter,
    visiblePrioritizeSections
  });

  // After 400ms, perform the actual delete when a task is pending delete (highlight first, then delete)
  useEffect(() => {
    if (pendingDeleteTaskId == null) return;
    const origin = pendingDeleteOrigin;
    const timeoutId = setTimeout(() => {
      handleDeleteTask(pendingDeleteTaskId, null, origin);
      setPendingDeleteTaskId(null);
      setPendingDeleteOrigin(null);
    }, 400);
    return () => clearTimeout(timeoutId);
  }, [pendingDeleteTaskId, pendingDeleteOrigin, handleDeleteTask]);

  // Intercept due date updates for placeholder so we update local state and UI; real tasks go to context
  const handleUpdateTaskDueDate = useCallback((taskId, newDueDate, isLaterDate = false, origin) => {
    if (taskId === 'placeholder') {
      setPlaceholderDueDate(newDueDate || null);
      setPlaceholderIsLaterDate(!!(newDueDate && isLaterDate));
      return;
    }
    updateTaskDueDate(taskId, newDueDate, isLaterDate, origin);
  }, [updateTaskDueDate]);

  const handleTaskTextInputWithPlaceholderContent = useCallback((clientId, e) => {
    handleTaskTextInput(clientId, e);
    if (clientId === 'placeholder_client_key' && e?.target) {
      const text = (e.target.textContent || '').trim();
      setPlaceholderHasContent(text.length >= 1);
    }
  }, [handleTaskTextInput]);

  // React-driven focus: run after commit when editingTaskId or focusRequestId changes (no setTimeout)
  useEffect(() => {
    tryFocusPending();
  }, [editingTaskId, focusRequestId, tryFocusPending]);

  // Task status hook
  const { handleCheckboxChange: handleCheckboxChangeBase } = useTaskStatus({
    tasks,
    updateTaskStatus
  });

  // In Prioritize view, keep completed tasks visible until completion animation + 200ms
  const handleCheckboxChange = useCallback((taskId, newStatusOrCurrentStatus) => {
    if (taskId === 'placeholder') {
      const validStatuses = ['complete', 'incomplete', 'wip', 'waiting', 'wont_do'];
      if (typeof newStatusOrCurrentStatus === 'string' && validStatuses.includes(newStatusOrCurrentStatus)) {
        setPlaceholderStatus(newStatusOrCurrentStatus);
      } else {
        setPlaceholderStatus(prev => (prev === 'complete' || prev === 'wip' || prev === 'waiting' || prev === 'wont_do' ? 'incomplete' : 'complete'));
      }
      return;
    }
    const validStatuses = ['complete', 'incomplete', 'wip', 'waiting', 'wont_do'];
    const isCompleting = typeof newStatusOrCurrentStatus === 'string' && newStatusOrCurrentStatus === 'complete';
    if (filter === 'prioritize' && isCompleting) {
      setRecentlyCompletedPrioritizeIds(prev => new Set(prev).add(taskId));
      const existing = prioritizeCompletionTimeoutsRef.current.get(taskId);
      if (existing) clearTimeout(existing);
      const timeoutId = setTimeout(() => {
        prioritizeCompletionTimeoutsRef.current.delete(taskId);
        setRecentlyCompletedPrioritizeIds(prev => {
          const next = new Set(prev);
          next.delete(taskId);
          return next;
        });
        // After animation: place this task first in Done section (use current state from refs)
        if (visiblePrioritizeSectionsRef.current.has('done') && updateTaskOrders) {
          const currentFileTasks = tasksRef.current.filter(t => String(t.file_id) === String(currentFileIdRef.current));
          const isLeaf = (task) => !currentFileTasks.some(t => t.parent_task_id === task.id);
          const doneTasks = currentFileTasks.filter(t => t.status === 'complete' && isLeaf(t));
          // Exclude the task we just completed so we don't overwrite its prioritize_order: 0
          const otherDoneTasks = doneTasks.filter(t => t.id !== taskId);
          const orderUpdates = [
            { id: taskId, prioritize_order: 0 },
            ...otherDoneTasks.map(t => ({ id: t.id, prioritize_order: (t.prioritize_order ?? 0) + 1 }))
          ];
          updateTaskOrders(orderUpdates, true).catch(() => {});
        }
      }, PRIORITIZE_COMPLETION_VISIBLE_MS);
      prioritizeCompletionTimeoutsRef.current.set(taskId, timeoutId);
    }
    handleCheckboxChangeBase(taskId, newStatusOrCurrentStatus);
  }, [filter, handleCheckboxChangeBase, updateTaskOrders]);

  // Toolbar indent/unindent: simulate Tab / Shift+Tab so behavior matches keyboard
  const handleIndent = useCallback((task) => {
    const el = document.querySelector(`.task-text[data-react-key="${task._reactKey}"]`);
    if (!el) return;
    const e = { key: 'Tab', shiftKey: false, preventDefault: () => {}, target: el };
    handleTaskTextKeyDown(e, task._reactKey);
  }, [handleTaskTextKeyDown]);

  const handleUnindent = useCallback((task) => {
    const el = document.querySelector(`.task-text[data-react-key="${task._reactKey}"]`);
    if (!el) return;
    const e = { key: 'Tab', shiftKey: true, preventDefault: () => {}, target: el };
    handleTaskTextKeyDown(e, task._reactKey);
  }, [handleTaskTextKeyDown]);

  // Blocking tasks highlight hook
  const {
    blockingTasksHighlight,
    handleBlockedIconMouseEnter,
    handleBlockedIconMouseLeave
  } = useBlockingTasksHighlight({
    tasks
  });

  // Helper functions for date calculations (used by prioritize drag-and-drop)
  const getTodayDate = useCallback(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }, []);

  const getLaterDate = useCallback(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // Random number of days between 7 and 14 (1-2 weeks)
    const randomDays = Math.floor(Math.random() * 8) + 7; // 7-14 days
    
    // Start from randomDays in the future
    const laterDate = new Date(today);
    laterDate.setDate(today.getDate() + randomDays);
    
    // Ensure it's a weekday (Monday = 1, Friday = 5)
    // getDay() returns: 0 = Sunday, 1 = Monday, ..., 6 = Saturday
    let dayOfWeek = laterDate.getDay();
    
    // If it's a weekend, move to the next Monday
    if (dayOfWeek === 0) {
      // Sunday: move forward 1 day to Monday
      laterDate.setDate(laterDate.getDate() + 1);
    } else if (dayOfWeek === 6) {
      // Saturday: move forward 2 days to Monday
      laterDate.setDate(laterDate.getDate() + 2);
    }
    
    const year = laterDate.getFullYear();
    const month = String(laterDate.getMonth() + 1).padStart(2, '0');
    const day = String(laterDate.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }, []);

  // Prioritize drag and drop hook (for Prioritize view)
  const prioritizeDragDrop = usePrioritizeDragDrop({
    tasks,
    currentFileId,
    updateTaskOrders,
    fetchTasks,
    getTodayDate,
    getLaterDate,
    visiblePrioritizeSections,
    updateTaskStatus
  });

  // Memoize the task tree (needed for first/last root keys used by drag-drop sentinels at list start/end)
  const loggedOutWorkspaceSnapshot = useMemo(() => {
    if (isLoggedIn || typeof getLoggedOutWorkspaceSnapshot !== 'function') return null;
    return getLoggedOutWorkspaceSnapshot();
  }, [isLoggedIn, getLoggedOutWorkspaceSnapshot, files, currentFileId, tasks]);

  const currentFileTasks = useMemo(() => {
    return tasks.filter(t => String(t.file_id) === String(currentFileId));
  }, [tasks, currentFileId]);

  useEffect(() => {
    if (currentFileTasks.length > 0) {
      setPlaceholderStatus('incomplete');
      setPlaceholderDueDate(null);
      setPlaceholderIsLaterDate(false);
    }
  }, [currentFileTasks.length]);

  const taskTree = useMemo(() => buildTaskTree(currentFileTasks), [currentFileTasks]);
  const filteredTaskTree = useMemo(() => filterTaskTree(taskTree, filter, timeFilter), [taskTree, filter, timeFilter]);
  const firstRootTaskKey = filteredTaskTree.length > 0 ? filteredTaskTree[0]._reactKey : null;
  const lastRootTaskKey = filteredTaskTree.length > 0 ? filteredTaskTree[filteredTaskTree.length - 1]._reactKey : null;

  const standardDragDrop = useTaskDragDropWithSequential({
    tasks,
    currentFileId,
    updateTaskOrders,
    fetchTasks,
    firstRootTaskKey,
    lastRootTaskKey
  });

  // Use prioritize drag handler when in prioritize view, otherwise use standard
  const isPrioritizeView = filter === 'prioritize';
  const dragDropResult = isPrioritizeView ? prioritizeDragDrop : standardDragDrop;
  const {
    sensors,
    collisionDetection,
    handleDragOver,
    handleDragEnd,
    handleDragCancel,
    dragOverTaskId,
    dropPosition
  } = dragDropResult;
  const dragOverSection = dragDropResult.dragOverSection || null;
  const dragOverSectionHeader = dragDropResult.dragOverSectionHeader ?? null;

  // Track task drag for mobile overflow lock (body class + touchmove preventDefault)
  const [isTaskDragging, setIsTaskDragging] = useState(false);
  const preventScrollRef = useRef(null);
  if (preventScrollRef.current == null) {
    preventScrollRef.current = (e) => {
      if (e.cancelable) e.preventDefault();
    };
  }
  const handleDragStart = useCallback(() => {
    setIsTaskDragging(true);
    document.body.classList.add('task-dragging-mobile');
    document.addEventListener('touchmove', preventScrollRef.current, { passive: false });
  }, []);
  const removeDragScrollLock = useCallback(() => {
    document.body.classList.remove('task-dragging-mobile');
    document.removeEventListener('touchmove', preventScrollRef.current);
  }, []);
  const handleDragEndWrapped = useCallback((...args) => {
    removeDragScrollLock();
    setIsTaskDragging(false);
    handleDragEnd(...args);
  }, [handleDragEnd, removeDragScrollLock]);
  const handleDragCancelWrapped = useCallback((...args) => {
    removeDragScrollLock();
    setIsTaskDragging(false);
    handleDragCancel(...args);
  }, [handleDragCancel, removeDragScrollLock]);
  useEffect(() => {
    if (!isTaskDragging) return;
    return () => removeDragScrollLock();
  }, [isTaskDragging, removeDragScrollLock]);

  // When dragging over a prioritize section header, add body class for dragged-item opacity
  useEffect(() => {
    if (isTaskDragging && dragOverSectionHeader) {
      document.body.classList.add('task-dragging-over-section-header');
      return () => document.body.classList.remove('task-dragging-over-section-header');
    }
    document.body.classList.remove('task-dragging-over-section-header');
  }, [isTaskDragging, dragOverSectionHeader]);

  // Auto-focus hook
  useTaskAutoFocus({
    tasksLength: tasks.length,
    loading,
    editingTaskId,
    setEditingTaskId
  });

  const handleMakeSequential = useCallback((task) => {
    if (isSequentialTask(task)) return;

    const isPlaceholder = task.id === 'placeholder' || task._reactKey === 'placeholder_client_key';
    if (isPlaceholder) {
      const status = placeholderStatusRef?.current ?? 'incomplete';
      const placeholderText =
        editingTaskId === task._reactKey && editingElementRef.current
          ? (editingElementRef.current.textContent || '').trim()
          : (task.text || '').trim();
      const firstTaskText = placeholderText ? '1.\u00A0' + placeholderText : '1.\u00A0';
      const firstTask = createTask(firstTaskText, null, 1, status, null, null);
      if (firstTask) {
        createTask('2.\u00A0', null, 2, 'incomplete', null, null);
        setEditingTaskId(firstTask._reactKey);
      }
      return;
    }

    const currentFileTasksList = tasks.filter(t => String(t.file_id) === String(currentFileId));
    const currentText = (task.text || '').trim();
    const currentHadText = currentText.length > 0;
    const prevTask = findPreviousSibling(task.id, currentFileTasksList);
    const seqNum =
      prevTask && isSequentialTask(prevTask) && getSequenceNumber(prevTask) != null
        ? getSequenceNumber(prevTask) + 1
        : 1;
    const newCurrentText = seqNum + '.\u00A0' + (task.text || '').trim();
    updateTaskText(task.id, newCurrentText);

    if (editingTaskId === task._reactKey && editingElementRef.current) {
      const el = editingElementRef.current;
      el.textContent = newCurrentText;
      const sel = window.getSelection();
      if (sel) {
        const range = document.createRange();
        range.selectNodeContents(el);
        range.collapse(false);
        sel.removeAllRanges();
        sel.addRange(range);
      }
    }

    const nextTask = findNextSibling(task.id, currentFileTasksList);
    if (nextTask) {
      if (!isSequentialTask(nextTask)) {
        const nextText = (nextTask.text || '').trim();
        const newNextText = (seqNum + 1) + '.\u00A0' + nextText;
        updateTaskText(nextTask.id, newNextText);
        if (editingTaskId === nextTask._reactKey && editingElementRef.current) {
          editingElementRef.current.textContent = newNextText;
        }
      }
    } else {
      const order = calculateOrderForParent(task.parent_task_id ?? null, currentFileTasksList, task.id);
      const newTask = createTask((seqNum + 1) + '.\u00A0', task.parent_task_id ?? null, order);
      if (newTask) {
        if (currentHadText) {
          setEditingTaskId(newTask._reactKey);
        } else {
          setEditingTaskId(task._reactKey);
        }
      }
    }
  }, [tasks, currentFileId, updateTaskText, createTask, setEditingTaskId, editingTaskId, editingElementRef, placeholderStatusRef]);

  const handleRemoveSequential = useCallback((task) => {
    if (!isSequentialTask(task)) return;
    const currentSeqNum = getSequenceNumber(task);
    const newText = (task.text || '').replace(/^\s*(\d+)\.\s*/, '').trimStart();
    updateTaskText(task.id, newText);

    if (editingTaskId === task._reactKey && editingElementRef.current) {
      const el = editingElementRef.current;
      el.textContent = newText;
      const sel = window.getSelection();
      if (sel) {
        const range = document.createRange();
        range.selectNodeContents(el);
        range.collapse(false);
        sel.removeAllRanges();
        sel.addRange(range);
      }
    }

    setEditingTaskId(task._reactKey);

    const currentFileTasksList = tasks.filter(t => String(t.file_id) === String(currentFileId));
    const nextTask = findNextSibling(task.id, currentFileTasksList);
    if (nextTask) {
      const nextSeqNum = getSequenceNumber(nextTask);
      const taskAfterNext = findNextSibling(nextTask.id, currentFileTasksList);
      const hasThreeOrMore =
        taskAfterNext &&
        isSequentialTask(taskAfterNext) &&
        getSequenceNumber(taskAfterNext) === 3;
      if (
        currentSeqNum === 1 &&
        nextSeqNum === 2 &&
        !hasThreeOrMore
      ) {
        const newNextText = (nextTask.text || '').replace(/^\s*(\d+)\.\s*/, '').trimStart();
        if (newNextText === '') {
          handleDeleteTask(nextTask.id);
        } else {
          updateTaskText(nextTask.id, newNextText);
          if (editingTaskId === nextTask._reactKey && editingElementRef.current) {
            editingElementRef.current.textContent = newNextText;
          }
        }
      } else {
        const nextText = (nextTask.text || '').trim();
        if (/^(\d+)\.\s*$/.test(nextText)) {
          handleDeleteTask(nextTask.id);
        }
      }
    }
  }, [tasks, currentFileId, updateTaskText, setEditingTaskId, editingTaskId, editingElementRef, handleDeleteTask]);

  const handleAddTaskAfterRoot = useCallback((sequential = false) => {
    const order = calculateOrderForParent(null, currentFileTasks);
    let initialText = '';
    if (sequential && currentFileTasks.length > 0) {
      const lastTask = currentFileTasks[currentFileTasks.length - 1];
      if (isSequentialTask(lastTask)) {
        const nextNum = getSequenceNumber(lastTask) + 1;
        initialText = nextNum + '.\u00A0';
      }
    }
    const newTask = createTask(initialText, null, order);
    if (newTask) setEditingTaskId(newTask._reactKey);
  }, [currentFileTasks, createTask, setEditingTaskId]);

  const handleAddTaskInPrioritizeSection = useCallback((section, options = {}) => {
    if (!section) return;

    // Do not set prioritize_order on new tasks: section sort places any task with
    // prioritize_order before all tasks without it, so a lone prioritize_order would
    // pin the new row to the top. Rely on root `order` (end of file) + tiebreakers instead.
    const order = calculateOrderForParent(null, currentFileTasks);
    const { initialText = '' } = options;
    let status = 'incomplete';
    let dueDate = null;
    let isLaterDate = false;

    if (section === 'today') {
      dueDate = getTodayDate();
    } else if (section === 'later') {
      dueDate = getLaterDate();
      isLaterDate = true;
    } else if (section === 'work-in-progress') {
      status = 'wip';
    } else if (section === 'done') {
      status = 'complete';
    }

    const newTask = createTask(
      initialText,
      null,
      order,
      status,
      dueDate,
      null,
      isLaterDate,
      { prioritizeSection: section }
    );
    if (!newTask) return;

    setEditingTaskId(newTask._reactKey);
    return newTask;
  }, [
    currentFileTasks,
    getTodayDate,
    getLaterDate,
    createTask,
    setEditingTaskId
  ]);

  const handleAddSubtask = useCallback((task, origin) => {
    const isPlaceholder = task.id === 'placeholder' || task._reactKey === 'placeholder_client_key';
    const prioritizeCreateOrigin =
      filter === 'prioritize'
        ? {
            prioritizeSection: getEffectivePrioritizeSection(
              task,
              visiblePrioritizeSections
            )
          }
        : undefined;

    if (isPlaceholder) {
      const placeholderText =
        editingTaskId === 'placeholder_client_key' && editingElementRef.current
          ? (editingElementRef.current.textContent || '').trim()
          : (task.text || '').trim();
      const status = placeholderStatusRef?.current ?? 'incomplete';
      const dueDate = placeholderDueDateRef?.current ?? null;
      const parentTask = createTask(
        placeholderText,
        null,
        1,
        status,
        dueDate,
        null,
        placeholderIsLaterDateRef?.current === true,
        prioritizeCreateOrigin
      );
      if (!parentTask) return;
      const subtask = createTask(
        '',
        parentTask.id,
        1,
        'incomplete',
        null,
        null,
        false,
        prioritizeCreateOrigin
      );
      if (!subtask) return;
      updateTaskStatus(parentTask.id, 'expanded');
      if (placeholderText !== '') {
        setEditingTaskId(subtask._reactKey);
      } else {
        setEditingTaskId(parentTask._reactKey);
      }
      return;
    }

    const order = calculateOrderForParent(task.id, currentFileTasks);
    const createOrigin = prioritizeCreateOrigin ?? origin;
    const newTask = createTask(
      '',
      task.id,
      order,
      'incomplete',
      null,
      null,
      false,
      createOrigin
    );
    if (!newTask) return;
    setExpandedTasks(prev => {
      const next = new Set(prev);
      next.add(task._reactKey);
      return next;
    });
    const currentTaskText = (task.text || '').trim();
    if (currentTaskText !== '') {
      setEditingTaskId(newTask._reactKey);
    }
  }, [
    currentFileTasks,
    createTask,
    filter,
    visiblePrioritizeSections,
    setExpandedTasks,
    setEditingTaskId,
    updateTaskStatus,
    editingTaskId,
    editingElementRef,
    placeholderStatusRef,
    placeholderDueDateRef,
    placeholderIsLaterDateRef
  ]);

  // Render task function
  const renderTaskRef = useRef(null);
  
  const renderTask = useCallback((task) => {
      // Ensure task always has a client-side ID (should never happen, but safety check)
      if (!task._reactKey) {
        console.error('Task missing _reactKey:', task);
        return null; // Don't render if missing _reactKey
      }
      return (
      <TaskRow
        key={task._reactKey} 
        task={task}
        tasks={tasks}
        createTask={createTask}
        expandedTasks={expandedTasks}
        editingTaskId={editingTaskId}
        editingDueDateTaskId={editingDueDateTaskId}
        blockingTasksHighlight={blockingTasksHighlight}
        topToolbarFlag={topToolbarFlag}
        filter={filter}
        timeFilter={timeFilter}
        editingElementRef={editingElementRef}
        editHighlightAnchorRef={editHighlightAnchorRef}
        onEditHighlightAnchorReady={onEditHighlightAnchorReady}
        dateInputCallbackRef={dateInputCallbackRef}
        onToggleExpand={toggleExpand}
        onBlockedIconMouseEnter={handleBlockedIconMouseEnter}
        onBlockedIconMouseLeave={handleBlockedIconMouseLeave}
        onCheckboxChange={handleCheckboxChange}
        onSetEditingTaskId={setEditingTaskId}
        onSetEditingDueDateTaskId={setEditingDueDateTaskId}
        onUpdateTaskDueDate={handleUpdateTaskDueDate}
        onTaskTextClick={handleTaskTextClick}
        onTaskTextFocus={handleTaskTextFocus}
        onTaskTextInput={handleTaskTextInputWithPlaceholderContent}
        onTaskContentClick={handleTaskContentClick}
        onTaskTextBlur={handleTaskTextBlur}
        onTaskTextKeyDown={handleTaskTextKeyDown}
        onTaskTextPaste={handleTaskTextPaste}
        renderTask={renderTaskRef.current}
        dragOverTaskId={dragOverTaskId}
        dropPosition={dropPosition}
        currentFileId={currentFileId}
        onUpdateTaskOrders={updateTaskOrders}
        recentlyCompletedPrioritizeIds={recentlyCompletedPrioritizeIds}
        recentlyMovedPrioritizeIds={recentlyMovedPrioritizeIds}
        visiblePrioritizeSections={visiblePrioritizeSections}
        onFocusTaskAfterStatusSelect={undefined}
        onIndent={handleIndent}
        onUnindent={handleUnindent}
        onMakeSequential={handleMakeSequential}
        onRemoveSequential={handleRemoveSequential}
        onAddSubtask={handleAddSubtask}
        onRequestDeleteTask={(task, origin) => {
          setPendingDeleteTaskId(task.id);
          setPendingDeleteOrigin(origin ?? null);
        }}
        pendingDeleteTaskId={pendingDeleteTaskId}
        skipPlaceholderCaretToStartRef={skipPlaceholderCaretToStartRef}
      />
    );
  }, [
    tasks,
    expandedTasks,
    editingTaskId,
    editingDueDateTaskId,
    filter,
    timeFilter,
    topToolbarFlag,
    editingElementRef,
    editHighlightAnchorRef,
    onEditHighlightAnchorReady,
    dateInputCallbackRef,
    toggleExpand,
    handleCheckboxChange,
    setEditingTaskId,
    setEditingDueDateTaskId,
    handleUpdateTaskDueDate,
    handleTaskTextClick,
    handleTaskTextFocus,
    handleTaskTextInput,
    handleTaskContentClick,
    handleTaskTextBlur,
    handleTaskTextKeyDown,
    handleTaskTextPaste,
    blockingTasksHighlight,
    handleBlockedIconMouseEnter,
    handleBlockedIconMouseLeave,
    dragOverTaskId,
    dropPosition,
    currentFileId,
    updateTaskOrders,
    recentlyCompletedPrioritizeIds,
    recentlyMovedPrioritizeIds,
    visiblePrioritizeSections,
    handleIndent,
    handleUnindent,
    createTask,
    handleMakeSequential,
    handleRemoveSequential,
    handleAddSubtask,
    pendingDeleteTaskId,
    skipPlaceholderCaretToStartRef
  ]);
  
  renderTaskRef.current = renderTask;

  // Group tasks by prioritize sections if prioritize filter is active
  const prioritizeGroups = useMemo(() => {
    if (filter === 'prioritize') {
      return groupTasksByPrioritize(taskTree, currentFileTasks, recentlyCompletedPrioritizeIds, visiblePrioritizeSections);
    }
    return null;
  }, [filter, taskTree, currentFileTasks, recentlyCompletedPrioritizeIds, visiblePrioritizeSections]);

  // Detect tasks that moved to a different section and highlight them briefly
  useEffect(() => {
    if (filter !== 'prioritize' || !prioritizeGroups) {
      previousPrioritizeSectionByTaskIdRef.current = new Map();
      if (filter !== 'prioritize') {
        prioritizeMovedTimeoutsRef.current.forEach(id => clearTimeout(id));
        prioritizeMovedTimeoutsRef.current.clear();
        setRecentlyMovedPrioritizeIds(new Set());
      }
      return;
    }
    const current = new Map();
    Object.entries(prioritizeGroups).forEach(([section, taskList]) => {
      (taskList || []).forEach(task => current.set(task.id, section));
    });
    const prev = previousPrioritizeSectionByTaskIdRef.current;
    const movedIds = [];
    current.forEach((section, taskId) => {
      const prevSection = prev.get(taskId);
      if (prevSection !== undefined && prevSection !== section) movedIds.push(taskId);
    });
    previousPrioritizeSectionByTaskIdRef.current = current;
    if (movedIds.length > 0) {
      setRecentlyMovedPrioritizeIds(prevSet => {
        const next = new Set(prevSet);
        movedIds.forEach(id => next.add(id));
        return next;
      });
      const HIGHLIGHT_MS = 1600;
      movedIds.forEach(taskId => {
        const existing = prioritizeMovedTimeoutsRef.current.get(taskId);
        if (existing) clearTimeout(existing);
        const timeoutId = setTimeout(() => {
          prioritizeMovedTimeoutsRef.current.delete(taskId);
          setRecentlyMovedPrioritizeIds(prevSet => {
            const next = new Set(prevSet);
            next.delete(taskId);
            return next;
          });
        }, HIGHLIGHT_MS);
        prioritizeMovedTimeoutsRef.current.set(taskId, timeoutId);
      });
    }
  }, [filter, prioritizeGroups]);

  if (loading) {
    return (
      <>
        {isLoggedIn && (
          <TaskFilters
            tasks={[]}
            modeSwitchInToolbar={topToolbarFlag}
          />
        )}
        <div className="task-table-loading">
          <span className="task-table-loading-text">Loading...</span>
        </div>
      </>
    );
  }

  // Render prioritize view (logged in or logged out with showcase layout)
  if (filter === 'prioritize' && prioritizeGroups) {
    const prioritizeView = (
      <>
        <TaskTablePrioritizeView
        tasks={tasks}
        prioritizeGroups={prioritizeGroups}
        visiblePrioritizeSections={visiblePrioritizeSections}
        togglePrioritizeSectionVisibility={togglePrioritizeSectionVisibility}
        currentFileName={currentFileName}
        editingFileName={editingFileName}
        onFileNameClick={handleFileNameClick}
        onFileNameBlur={handleFileNameBlur}
        onFileNameKeyDown={handleFileNameKeyDown}
        currentFileId={currentFileId}
        files={files}
        currentFile={files.find(f => f.id === currentFileId) || null}
        hasUnsavedChanges={hasPendingChangesForCurrentFile}
        hasRestoredDraft={hasRestoredDraft}
        loggedOutWorkspaceSnapshot={loggedOutWorkspaceSnapshot}
        sensors={sensors}
        collisionDetection={collisionDetection}
        onDragOver={handleDragOver}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEndWrapped}
        onDragCancel={handleDragCancelWrapped}
        expandedTasks={expandedTasks}
        editingTaskId={editingTaskId}
        editingDueDateTaskId={editingDueDateTaskId}
        blockingTasksHighlight={blockingTasksHighlight}
        filter={filter}
        onFilterChange={setFilter}
        topToolbarFlag={topToolbarFlag}
        timeFilter={timeFilter}
        editingElementRef={editingElementRef}
        editHighlightAnchorRef={editHighlightAnchorRef}
        onEditHighlightAnchorReady={onEditHighlightAnchorReady}
        dateInputCallbackRef={dateInputCallbackRef}
        onToggleExpand={toggleExpand}
        onCheckboxChange={handleCheckboxChange}
        onSetEditingTaskId={setEditingTaskId}
        onSetEditingDueDateTaskId={setEditingDueDateTaskId}
        onUpdateTaskDueDate={updateTaskDueDate}
        onTaskTextClick={handleTaskTextClick}
        onTaskTextFocus={handleTaskTextFocus}
        onTaskTextInput={handleTaskTextInputWithPlaceholderContent}
        onTaskContentClick={handleTaskContentClick}
        onTaskTextBlur={handleTaskTextBlur}
        onTaskTextKeyDown={handleTaskTextKeyDown}
        onTaskTextPaste={handleTaskTextPaste}
        onBlockedIconMouseEnter={handleBlockedIconMouseEnter}
        onBlockedIconMouseLeave={handleBlockedIconMouseLeave}
        dragOverTaskId={dragOverTaskId}
        dropPosition={dropPosition}
        dragOverSection={dragOverSection}
        dragOverSectionHeader={dragOverSectionHeader}
        isDragActive={isTaskDragging}
        renderTask={renderTask}
        onAddTaskInPrioritizeSection={handleAddTaskInPrioritizeSection}
        isLoggedIn={isLoggedIn}
      />
      </>
    );
    if (!isLoggedIn && currentFileId === 'default') {
      return (
        <>
          {prioritizeView}
          <FeatureShowcase
            isMinimized={isShowcaseMinimized}
            onMinimizedChange={setIsShowcaseMinimized}
          />
        </>
      );
    }
    return prioritizeView;
  }

  // When logged out on default file, show task list + Help button (expanded view is a modal)
  if (!isLoggedIn && currentFileId === 'default') {
    return (
      <>
        <TaskTableStandardView
          filter={filter}
          timeFilter={timeFilter}
          onFilterChange={setFilter}
          onTimeFilterChange={setTimeFilter}
          visiblePrioritizeSections={visiblePrioritizeSections}
          togglePrioritizeSectionVisibility={togglePrioritizeSectionVisibility}
          tasks={tasks}
          filteredTaskTree={filteredTaskTree}
          currentFileName={currentFileName}
          editingFileName={editingFileName}
          onFileNameClick={handleFileNameClick}
          onFileNameBlur={handleFileNameBlur}
          onFileNameKeyDown={handleFileNameKeyDown}
          currentFileId={currentFileId}
          files={files}
          currentFile={files.find(f => f.id === currentFileId) || null}
          hasUnsavedChanges={hasPendingChangesForCurrentFile}
          hasRestoredDraft={hasRestoredDraft}
          loggedOutWorkspaceSnapshot={loggedOutWorkspaceSnapshot}
          sensors={sensors}
          collisionDetection={collisionDetection}
          onDragOver={handleDragOver}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEndWrapped}
          onDragCancel={handleDragCancelWrapped}
          expandedTasks={expandedTasks}
          editingTaskId={editingTaskId}
          editingDueDateTaskId={editingDueDateTaskId}
          blockingTasksHighlight={blockingTasksHighlight}
          editingElementRef={editingElementRef}
          editHighlightAnchorRef={editHighlightAnchorRef}
          onEditHighlightAnchorReady={onEditHighlightAnchorReady}
          dateInputCallbackRef={dateInputCallbackRef}
          onToggleExpand={toggleExpand}
          onCheckboxChange={handleCheckboxChange}
          onSetEditingTaskId={setEditingTaskId}
          onSetEditingDueDateTaskId={setEditingDueDateTaskId}
          onUpdateTaskDueDate={handleUpdateTaskDueDate}
          onTaskTextClick={handleTaskTextClick}
          onTaskTextFocus={handleTaskTextFocus}
          onTaskTextInput={handleTaskTextInputWithPlaceholderContent}
          onTaskContentClick={handleTaskContentClick}
          onTaskTextBlur={handleTaskTextBlur}
          onTaskTextKeyDown={handleTaskTextKeyDown}
          onTaskTextPaste={handleTaskTextPaste}
          onBlockedIconMouseEnter={handleBlockedIconMouseEnter}
          onBlockedIconMouseLeave={handleBlockedIconMouseLeave}
          renderTask={renderTask}
          dragOverTaskId={dragOverTaskId}
          dropPosition={dropPosition}
          placeholderStatus={placeholderStatus}
          placeholderDueDate={placeholderDueDate}
          placeholderIsLaterDate={placeholderIsLaterDate}
          placeholderHasContent={placeholderHasContent}
          onAddTaskAfterRoot={handleAddTaskAfterRoot}
          onIndent={handleIndent}
          onUnindent={handleUnindent}
          onMakeSequential={handleMakeSequential}
          onRemoveSequential={handleRemoveSequential}
          onAddSubtask={handleAddSubtask}
          onRequestDeleteTask={(task, origin) => {
            setPendingDeleteTaskId(task.id);
            setPendingDeleteOrigin(origin ?? null);
          }}
          topToolbarFlag={topToolbarFlag}
          isLoggedIn={isLoggedIn}
        />
        <FeatureShowcase
          isMinimized={isShowcaseMinimized}
          onMinimizedChange={setIsShowcaseMinimized}
        />
      </>
    );
  }

  // Render standard view
  return (
    <>
          <TaskTableStandardView
        filter={filter}
        timeFilter={timeFilter}
        onFilterChange={setFilter}
        onTimeFilterChange={setTimeFilter}
        visiblePrioritizeSections={visiblePrioritizeSections}
        togglePrioritizeSectionVisibility={togglePrioritizeSectionVisibility}
        tasks={tasks}
      filteredTaskTree={filteredTaskTree}
      currentFileName={currentFileName}
      editingFileName={editingFileName}
      onFileNameClick={handleFileNameClick}
      onFileNameBlur={handleFileNameBlur}
      onFileNameKeyDown={handleFileNameKeyDown}
      currentFileId={currentFileId}
      files={files}
      currentFile={files.find(f => f.id === currentFileId) || null}
      hasUnsavedChanges={hasPendingChangesForCurrentFile}
      hasRestoredDraft={hasRestoredDraft}
      loggedOutWorkspaceSnapshot={loggedOutWorkspaceSnapshot}
        sensors={sensors}
        collisionDetection={collisionDetection}
        onDragOver={handleDragOver}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEndWrapped}
        onDragCancel={handleDragCancelWrapped}
          expandedTasks={expandedTasks}
          editingTaskId={editingTaskId}
          editingDueDateTaskId={editingDueDateTaskId}
          blockingTasksHighlight={blockingTasksHighlight}
          editingElementRef={editingElementRef}
          editHighlightAnchorRef={editHighlightAnchorRef}
          onEditHighlightAnchorReady={onEditHighlightAnchorReady}
          dateInputCallbackRef={dateInputCallbackRef}
          onToggleExpand={toggleExpand}
          onCheckboxChange={handleCheckboxChange}
          onSetEditingTaskId={setEditingTaskId}
          onSetEditingDueDateTaskId={setEditingDueDateTaskId}
          onUpdateTaskDueDate={handleUpdateTaskDueDate}
          onTaskTextClick={handleTaskTextClick}
          onTaskTextFocus={handleTaskTextFocus}
          onTaskTextInput={handleTaskTextInputWithPlaceholderContent}
          onTaskContentClick={handleTaskContentClick}
          onTaskTextBlur={handleTaskTextBlur}
          onTaskTextKeyDown={handleTaskTextKeyDown}
          onTaskTextPaste={handleTaskTextPaste}
          onBlockedIconMouseEnter={handleBlockedIconMouseEnter}
          onBlockedIconMouseLeave={handleBlockedIconMouseLeave}
          renderTask={renderTask}
      dragOverTaskId={dragOverTaskId}
      dropPosition={dropPosition}
      placeholderStatus={placeholderStatus}
        placeholderDueDate={placeholderDueDate}
        placeholderIsLaterDate={placeholderIsLaterDate}
        placeholderHasContent={placeholderHasContent}
        onAddTaskAfterRoot={handleAddTaskAfterRoot}
        onIndent={handleIndent}
        onUnindent={handleUnindent}
        onMakeSequential={handleMakeSequential}
        onRemoveSequential={handleRemoveSequential}
        onAddSubtask={handleAddSubtask}
        onRequestDeleteTask={(task, origin) => {
          setPendingDeleteTaskId(task.id);
          setPendingDeleteOrigin(origin ?? null);
        }}
        topToolbarFlag={topToolbarFlag}
        isLoggedIn={isLoggedIn}
        />
    </>
  );
}

export default TaskTable;
